import { CapacitorUpdater } from "@capgo/capacitor-updater";
import type { OtaCheck, UpdaterPort } from "./types";

/**
 * OTA 수동 흐름(AC0-170). 자동 업데이트(autoUpdate)는 끄고, 부팅 중 스플래시에서 확인 → 진행률을 보이며 내려받기 → 즉시 적용(웹뷰 다시 로드).
 * 서버 응답(server-detail POST /updates/check): 번들 `{version,url,checksum}` · 내장 번들 복귀 `{version:'builtin'}` · 없음(kind up_to_date).
 */
export const nativeUpdater: UpdaterPort = {
  async check(timeoutMs) {
    const latest = CapacitorUpdater.getLatest().then(
      (r): OtaCheck => {
        if (r.version === "builtin") return "builtin";
        if (!r.url || !r.version || r.kind === "up_to_date" || r.error) return null;
        return { version: r.version, url: r.url, ...(r.checksum ? { checksum: r.checksum } : {}) };
      },
      () => null,
    );
    const timeout = new Promise<null>((r) => setTimeout(() => r(null), timeoutMs));
    const [result, current] = await Promise.all([Promise.race([latest, timeout]), CapacitorUpdater.current().catch(() => null)]);
    // 이미 적용된 번들이면 다시 받지 않는다(서버도 같은 판정을 하지만 플러그인 상태가 정본)
    if (result && result !== "builtin" && current?.bundle.version === result.version) return null;
    if (result === "builtin" && (!current || current.bundle.id === "builtin")) return null;
    return result;
  },
  async apply(update, onProgress) {
    const sub = await CapacitorUpdater.addListener("download", ({ percent }) => onProgress(percent));
    try {
      const bundle = await CapacitorUpdater.download({ url: update.url, version: update.version, ...(update.checksum ? { checksum: update.checksum } : {}) });
      onProgress(100);
      await CapacitorUpdater.set({ id: bundle.id }); // 적용 = 웹뷰 다시 로드. 새 번들이 notifyAppReady 를 못 부르면 플러그인이 되돌린다
    } finally {
      await sub.remove();
    }
  },
  async reset() {
    await CapacitorUpdater.reset();
  },
  async currentBundle() {
    const c = await CapacitorUpdater.current().catch(() => null);
    return c && c.bundle.id !== "builtin" ? c.bundle.version : null;
  },
};
